import { BeneficiaryAccountType, LegalEntityCountry } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { ComponentProps, useState } from 'react';
import { Stack } from '../../../design-system/stack/stack';
import { exhaustiveCheck } from '../../../utils/exhaustive-check';
import { InvoiceWarningCard } from '../bill-pay-flow/components/invoice-warning-card';
import { PaymentFlowStepWrapper } from '../components/payment-flow-step-wrapper';
import { AddressBeneficiaryForm, AddressBeneficiaryFormValues } from './components/address-beneficiary-form';
import { BeneficiaryCreationFormValues, CreateBeneficiaryForm } from './components/create-beneficiary-form';
import { getIsAddressRequired } from './utils/get-is-address-required';

type CreateBeneficiaryFlowStep = 'DETAILS' | 'ADDRESS';

export type CreateBeneficiaryFlowFormValues = BeneficiaryCreationFormValues & {
  address?: AddressBeneficiaryFormValues;
  accountType: BeneficiaryAccountType;
};

type CreateBeneficiaryFlowProps = {
  companyCountry: LegalEntityCountry;
  initialValues?: ComponentProps<typeof CreateBeneficiaryForm>['initialValues'];
  wrapperConfig?: ComponentProps<typeof CreateBeneficiaryForm>['wrapperConfig'];
  isWarningCardVisible?: boolean;
  isLoading: boolean;
  onCreateBeneficiary: (formValues: CreateBeneficiaryFlowFormValues) => Promise<void>;
  onGoBack: () => void;
};

export const CreateBeneficiaryFlow = ({
  companyCountry,
  initialValues,
  wrapperConfig,
  isWarningCardVisible = false,
  isLoading,
  onCreateBeneficiary,
  onGoBack,
}: CreateBeneficiaryFlowProps) => {
  const [step, setStep] = useState<CreateBeneficiaryFlowStep>('DETAILS');
  const [beneficiaryDetails, setBeneficiaryDetails] = useState<CreateBeneficiaryFlowFormValues | undefined>(undefined);

  const onSubmitBeneficiaryDetails = async ({
    detailsFormValues,
    accountType,
  }: Parameters<ComponentProps<typeof CreateBeneficiaryForm>['onSubmit']>[0]) => {
    const { country, currency } = detailsFormValues;

    const isAddressRequired = getIsAddressRequired({ beneficiaryCountry: country, companyCountry, currency });

    if (isAddressRequired && step === 'DETAILS') {
      setStep('ADDRESS');
      setBeneficiaryDetails({
        ...detailsFormValues,
        accountType,
      });

      return;
    }

    await onCreateBeneficiary({
      ...detailsFormValues,
      accountType,
    });
  };

  const onSubmitBeneficiaryAddress = (
    addressFormValues: Parameters<ComponentProps<typeof AddressBeneficiaryForm>['onSubmit']>[0],
  ) => {
    if (!beneficiaryDetails) {
      Sentry.captureMessage(
        'Beneficiary details not found when calling onSubmitBeneficiaryAddress in CreateBeneficiaryFlow',
        {
          extra: {
            beneficiaryDetails,
            addressFormValues,
            message: 'Beneficiary details not found when calling onSubmitBeneficiaryAddress in CreateBeneficiaryFlow',
          },
        },
      );

      return null;
    }

    onCreateBeneficiary({
      ...beneficiaryDetails,
      address: addressFormValues,
    });
  };

  switch (step) {
    case 'DETAILS':
      return (
        <PaymentFlowStepWrapper title="New Recipient" onGoBack={onGoBack}>
          <Stack space={3} align="stretch">
            {isWarningCardVisible && <InvoiceWarningCard variant="FIRST_TIME_RECIPIENT" />}

            <CreateBeneficiaryForm
              isLoading={isLoading}
              onSubmit={onSubmitBeneficiaryDetails}
              initialValues={initialValues}
              wrapperConfig={wrapperConfig}
            />
          </Stack>
        </PaymentFlowStepWrapper>
      );
    case 'ADDRESS':
      if (!beneficiaryDetails) {
        Sentry.captureMessage(
          'Beneficiary details not found when rendering address form in ADDRESS step in CreateBeneficiaryFlow',
          {
            extra: {
              step,
              beneficiaryDetails,
              message:
                'Beneficiary details not found when rendering address form in ADDRESS step in CreateBeneficiaryFlow',
            },
          },
        );

        return null;
      }

      return (
        <PaymentFlowStepWrapper title="Recipient's Address" onGoBack={() => setStep('DETAILS')}>
          <AddressBeneficiaryForm
            isLoading={isLoading}
            country={beneficiaryDetails?.country}
            onSubmit={onSubmitBeneficiaryAddress}
          />
        </PaymentFlowStepWrapper>
      );
    default:
      return exhaustiveCheck(step);
  }
};
